// Street furniture: instanced lamps / trees / benches / bins / traffic lights, billboards, neon, signs, parking spots.
import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { aabb, mat, QuadSoup, type BuildCtx } from './city_common';
import { HALF, SIDEWALK, CURB_H, LANE, type Layout } from './city_layout';
import type { TextAtlas } from './city_textures';

export interface PropsResult {
  meshes: THREE.Object3D[];
}

function colored(g: THREE.BufferGeometry, hex: number): THREE.BufferGeometry {
  const geo = g.index ? g.toNonIndexed() : g;
  const c = new THREE.Color(hex);
  const n = geo.attributes.position.count;
  const arr = new Float32Array(n * 3);
  for (let i = 0; i < n; i++) {
    arr[i * 3] = c.r;
    arr[i * 3 + 1] = c.g;
    arr[i * 3 + 2] = c.b;
  }
  geo.setAttribute('color', new THREE.BufferAttribute(arr, 3));
  return geo;
}

function mergeColored(parts: [THREE.BufferGeometry, THREE.Matrix4, number][]): THREE.BufferGeometry {
  const gs = parts.map(([g, m, c]) => {
    const cg = colored(g, c);
    cg.applyMatrix4(m);
    return cg;
  });
  const merged = mergeGeometries(gs, false)!;
  gs.forEach((g) => g.dispose());
  return merged;
}

function instanced(geo: THREE.BufferGeometry, mat: THREE.Material, items: THREE.Matrix4[], name: string, shadow = true): THREE.InstancedMesh {
  const im = new THREE.InstancedMesh(geo, mat, Math.max(1, items.length));
  items.forEach((m, i) => im.setMatrixAt(i, m));
  if (!items.length) im.count = 0;
  im.instanceMatrix.needsUpdate = true;
  im.computeBoundingSphere();
  im.castShadow = shadow;
  im.receiveShadow = false;
  im.name = name;
  im.frustumCulled = true;
  return im;
}

/** lamp placement along every block side + promenades + explicit requests */
function collectLamps(ctx: BuildCtx): { x: number; z: number; ry: number }[] {
  const L = ctx.layout;
  const out: { x: number; z: number; ry: number }[] = [];
  const IN = 0.75; // from the curb
  for (const b of L.blocks) {
    const sides: [number, number, number, number, number, number][] = [
      // x0,z0,x1,z1 of the line, arm dir x, dir z
      [b.ox0, b.oz0 + IN, b.ox1, b.oz0 + IN, 0, -1],
      [b.ox0, b.oz1 - IN, b.ox1, b.oz1 - IN, 0, 1],
      [b.ox0 + IN, b.oz0, b.ox0 + IN, b.oz1, -1, 0],
      [b.ox1 - IN, b.oz0, b.ox1 - IN, b.oz1, 1, 0],
    ];
    sides.forEach((s, si) => {
      const len = Math.hypot(s[2] - s[0], s[3] - s[1]);
      const n = Math.max(2, Math.round(len / 24));
      const phase = si === 0 || si === 2 ? 0.5 : 0.25;
      for (let i = 0; i < n; i++) {
        const t = (i + phase) / n;
        if (t <= 0.06 || t >= 0.94) continue;
        out.push({ x: s[0] + (s[2] - s[0]) * t, z: s[1] + (s[3] - s[1]) * t, ry: Math.atan2(s[4], s[5]) });
      }
    });
  }
  // promenades (river side of the embankment roads)
  const pxA = L.roadX[L.riverCol] + HALF + 1.0, pxB = L.roadX[L.riverCol + 1] - HALF - 1.0;
  for (let z = L.roadZ[0] + 4; z < L.roadZ[L.rows]; z += 24) {
    let nearBridge = false;
    for (const j of L.bridgeRows) if (Math.abs(z - L.roadZ[j]) < 12) nearBridge = true;
    if (nearBridge) continue;
    out.push({ x: pxA, z, ry: -Math.PI / 2 });
    out.push({ x: pxB, z, ry: Math.PI / 2 });
  }
  for (const r of ctx.lampRequests) out.push(r);
  return out;
}

export function buildProps(ctx: BuildCtx): PropsResult {
  const L = ctx.layout;
  const rng = ctx.rng;
  const T = ctx.tex;
  const meshes: THREE.Object3D[] = [];

  // ---- lamp posts ----------------------------------------------------------------
  const lamps = collectLamps(ctx);
  const poleGeo = mergeColored([
    [new THREE.CylinderGeometry(0.22, 0.26, 0.5, 8, 1, true), mat(0, 0.25, 0), 0x2b2e33],
    [new THREE.CylinderGeometry(0.07, 0.11, 6.6, 7, 1, true), mat(0, 3.8, 0), 0x3a3d42],
    [new THREE.CylinderGeometry(0.06, 0.06, 1.5, 5, 1, true), mat(0, 7.0, 0.7, 0, 1, 1, 1, Math.PI / 2), 0x3a3d42],
    [new THREE.BoxGeometry(0.55, 0.22, 0.34), mat(0, 7.0, 1.45), 0x2b2e33],
  ]);
  const poleMats = lamps.map((l) => mat(l.x, CURB_H, l.z, l.ry));
  const poleMat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.6, metalness: 0.3 });
  const poles = instanced(poleGeo, poleMat, poleMats, 'lampPosts');
  meshes.push(poles);
  ctx.raycast.push(poles);
  for (const l of lamps) {
    ctx.colliders.push(aabb(l.x - 0.18, l.z - 0.18, l.x + 0.18, l.z + 0.18, 7.5));
    ctx.lamps.push({ x: l.x + Math.sin(l.ry) * 1.45, z: l.z + Math.cos(l.ry) * 1.45 });
  }
  // bulbs (emissive) under the head
  const bulbGeo = new THREE.BoxGeometry(0.42, 0.1, 0.26);
  const bulbMat = new THREE.MeshStandardMaterial({ color: 0xfff1d6, emissive: 0xffc27a, emissiveIntensity: 0, roughness: 0.5 });
  const bulbs = instanced(bulbGeo, bulbMat, lamps.map((l) => mat(l.x + Math.sin(l.ry) * 1.45, CURB_H + 6.85, l.z + Math.cos(l.ry) * 1.45, l.ry)), 'lampBulbs', false);
  meshes.push(bulbs);
  // light cones (additive, vertex-alpha gradient via vertex colours)
  const coneGeo = new THREE.ConeGeometry(3.4, 6.6, 14, 1, true);
  {
    const pos = coneGeo.attributes.position;
    const col = new Float32Array(pos.count * 3);
    for (let i = 0; i < pos.count; i++) {
      const k = pos.getY(i) > 0 ? 1 : 0; // apex (top) bright, base dark
      col[i * 3] = k;
      col[i * 3 + 1] = k;
      col[i * 3 + 2] = k;
    }
    coneGeo.setAttribute('color', new THREE.BufferAttribute(col, 3));
  }
  const coneMat = new THREE.MeshBasicMaterial({ color: 0xffc27a, vertexColors: true, transparent: true, opacity: 0, blending: THREE.AdditiveBlending, depthWrite: false, side: THREE.DoubleSide, fog: false });
  const cones = instanced(coneGeo, coneMat, lamps.map((l) => mat(l.x + Math.sin(l.ry) * 1.45, CURB_H + 6.8 - 3.3, l.z + Math.cos(l.ry) * 1.45)), 'lampCones', false);
  cones.renderOrder = 5;
  meshes.push(cones);
  const poolGeo = new THREE.CircleGeometry(4.6, 18);
  poolGeo.rotateX(-Math.PI / 2);
  const poolMat = new THREE.MeshBasicMaterial({ map: T.lightPool, transparent: true, opacity: 0, blending: THREE.AdditiveBlending, depthWrite: false, fog: false });
  const pools = instanced(poolGeo, poolMat, lamps.map((l) => mat(l.x + Math.sin(l.ry) * 1.45, 0.02, l.z + Math.cos(l.ry) * 1.45)), 'lampPools', false);
  pools.renderOrder = 4;
  meshes.push(pools);
  ctx.nightItems.push((f) => {
    bulbMat.emissiveIntensity = f * 4.0;
    coneMat.opacity = f * 0.22;
    poolMat.opacity = f * 0.55;
    cones.visible = f > 0.02;
    pools.visible = f > 0.02;
  });

  // ---- trees ---------------------------------------------------------------------
  const treeParts: [THREE.BufferGeometry, THREE.Matrix4, number][] = [
    [new THREE.CylinderGeometry(0.13, 0.2, 2.8, 6, 1, true), mat(0, 1.4, 0), 0x5a4030],
    [new THREE.IcosahedronGeometry(1.7, 1), mat(0.1, 3.4, 0, 0, 1, 0.85, 1), 0x3d7a2c],
    [new THREE.IcosahedronGeometry(1.45, 0), mat(-0.5, 4.3, 0.4, 0.7, 1, 0.9, 1), 0x4a8c34],
    [new THREE.IcosahedronGeometry(1.2, 0), mat(0.4, 5.0, -0.3, 1.4, 1, 0.9, 1), 0x356b28],
  ];
  const treeGeo = mergeColored(treeParts);
  const trees: THREE.Matrix4[] = [];
  const treeTints: THREE.Color[] = [];
  const addTree = (x: number, z: number, s: number) => {
    trees.push(mat(x, CURB_H, z, rng() * Math.PI * 2, s, s * (0.9 + rng() * 0.3), s));
    treeTints.push(new THREE.Color().setHSL(0.25 + (rng() - 0.5) * 0.06, 0.55 + rng() * 0.2, 0.32 + rng() * 0.12));
    ctx.colliders.push(aabb(x - 0.28, z - 0.28, x + 0.28, z + 0.28, 3));
  };
  // sidewalk trees between lamps (curb side, 1.3 m in)
  for (const b of L.blocks) {
    const p = b.buda ? 0.6 : 0.42;
    const lines: [number, number, number, number][] = [
      [b.ox0, b.oz0 + 1.3, b.ox1, b.oz0 + 1.3],
      [b.ox0, b.oz1 - 1.3, b.ox1, b.oz1 - 1.3],
      [b.ox0 + 1.3, b.oz0, b.ox0 + 1.3, b.oz1],
      [b.ox1 - 1.3, b.oz0, b.ox1 - 1.3, b.oz1],
    ];
    for (const ln of lines) {
      if (rng() > p) continue;
      const len = Math.hypot(ln[2] - ln[0], ln[3] - ln[1]);
      const n = Math.max(2, Math.round(len / 12));
      for (let i = 0; i < n; i++) {
        const t = (i + 0.5) / n;
        if (rng() < 0.15) continue;
        addTree(ln[0] + (ln[2] - ln[0]) * t, ln[1] + (ln[3] - ln[1]) * t, 0.85 + rng() * 0.45);
      }
    }
  }
  for (const r of ctx.treeRequests) addTree(r.x, r.z, r.s);
  // outside the ring road: a loose belt of trees
  for (let i = 0; i < 140; i++) {
    const side = Math.floor(rng() * 4);
    const B = L.bounds;
    let x: number, z: number;
    if (side === 0) { x = B.minX - 4 - rng() * 60; z = B.minZ + rng() * (B.maxZ - B.minZ); }
    else if (side === 1) { x = B.maxX + 4 + rng() * 60; z = B.minZ + rng() * (B.maxZ - B.minZ); }
    else if (side === 2) { x = B.minX + rng() * (B.maxX - B.minX); z = B.minZ - 4 - rng() * 60; }
    else { x = B.minX + rng() * (B.maxX - B.minX); z = B.maxZ + 4 + rng() * 60; }
    if (L.isRiverX(x) || Math.abs(x - L.riverCx) < 45) continue;
    trees.push(mat(x, -0.05, z, rng() * 6.28, 1.2 + rng() * 0.8, 1.2 + rng() * 0.6, 1.2 + rng() * 0.8));
    treeTints.push(new THREE.Color().setHSL(0.27, 0.5, 0.3 + rng() * 0.1));
  }
  const treeMat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.95, metalness: 0 });
  const treeMesh = instanced(treeGeo, treeMat, trees, 'trees');
  treeTints.forEach((c, i) => treeMesh.setColorAt(i, c));
  if (treeMesh.instanceColor) treeMesh.instanceColor.needsUpdate = true;
  meshes.push(treeMesh);
  ctx.raycast.push(treeMesh);

  // ---- benches + bins -------------------------------------------------------------
  const benchGeo = mergeColored([
    [new THREE.BoxGeometry(1.8, 0.07, 0.5), mat(0, 0.46, 0), 0x6b4a2e],
    [new THREE.BoxGeometry(1.8, 0.42, 0.06), mat(0, 0.72, -0.24, 0, 1, 1, 1, -0.15), 0x6b4a2e],
    [new THREE.BoxGeometry(0.08, 0.45, 0.45), mat(-0.8, 0.22, 0), 0x2b2e33],
    [new THREE.BoxGeometry(0.08, 0.45, 0.45), mat(0.8, 0.22, 0), 0x2b2e33],
  ]);
  const benches: THREE.Matrix4[] = [];
  const binGeo = mergeColored([
    [new THREE.CylinderGeometry(0.28, 0.25, 0.9, 10), mat(0, 0.45, 0), 0x2f5a3a],
    [new THREE.CylinderGeometry(0.3, 0.3, 0.06, 10), mat(0, 0.93, 0), 0x1f1f22],
  ]);
  const bins: THREE.Matrix4[] = [];
  lamps.forEach((l, i) => {
    if (i % 4 === 1 && rng() < 0.7) {
      // bench facing the road next to the lamp
      const bx = l.x - Math.cos(l.ry) * 1.6, bz = l.z + Math.sin(l.ry) * 1.6;
      benches.push(mat(bx, CURB_H, bz, l.ry + Math.PI));
      ctx.colliders.push(aabb(bx - 0.9, bz - 0.3, bx + 0.9, bz + 0.3, 1));
    }
    if (i % 3 === 2 && rng() < 0.6) {
      const bx = l.x + Math.cos(l.ry) * 1.1, bz = l.z - Math.sin(l.ry) * 1.1;
      bins.push(mat(bx, CURB_H, bz));
      ctx.colliders.push(aabb(bx - 0.3, bz - 0.3, bx + 0.3, bz + 0.3, 1));
    }
  });
  for (const r of ctx.benchRequests) {
    benches.push(mat(r.x, CURB_H, r.z, r.ry));
    ctx.colliders.push(aabb(r.x - 0.9, r.z - 0.3, r.x + 0.9, r.z + 0.3, 1));
  }
  const propMat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.8, metalness: 0.1 });
  const benchMesh = instanced(benchGeo, propMat, benches, 'benches');
  const binMesh = instanced(binGeo, propMat, bins, 'bins');
  meshes.push(benchMesh, binMesh);
  ctx.raycast.push(benchMesh, binMesh);

  // ---- bus stops --------------------------------------------------------------------
  let stopIdx = 0;
  for (const s of L.segments) {
    if (s.bridge) continue;
    stopIdx++;
    if (stopIdx % 6 !== 0) continue;
    const len = s.dir === 'h' ? s.bx - s.ax : s.bz - s.az;
    if (len < 40) continue;
    const t = 0.62;
    // on the right-hand sidewalk of the positive direction
    let x: number, z: number, ry: number;
    if (s.dir === 'h') {
      x = s.ax + len * t;
      z = s.az + HALF + 1.6;
      ry = Math.PI; // shelter faces the road (-z)
    } else {
      x = s.ax - HALF - 1.6;
      z = s.az + len * t;
      ry = Math.PI / 2; // faces +x
    }
    const m = mat(x, CURB_H, z, ry);
    const parts: [THREE.BufferGeometry, THREE.Matrix4, number][] = [
      [new THREE.BoxGeometry(0.1, 2.6, 0.1), mat(-1.9, 1.3, -0.9), 0x3a3d42],
      [new THREE.BoxGeometry(0.1, 2.6, 0.1), mat(1.9, 1.3, -0.9), 0x3a3d42],
      [new THREE.BoxGeometry(4.2, 0.1, 2.0), mat(0, 2.65, -0.2), 0x3a3d42],
      [new THREE.BoxGeometry(4.0, 0.06, 0.45), mat(0, 0.5, -0.7), 0x6b4a2e],
      [new THREE.BoxGeometry(0.35, 0.5, 0.05), mat(1.6, 2.1, 0.2), 0x1e5aa8],
    ];
    for (const [g, pm, c] of parts) ctx.metal.add(g, pm.premultiply(m), c);
    ctx.glass.add(new THREE.BoxGeometry(3.9, 2.2, 0.06), mat(0, 1.35, -0.95).premultiply(m), 0x9ab8cc);
    ctx.colliders.push(aabb(x - 2.1, z - 1.2, x + 2.1, z + 1.2, 2.7));
  }

  // ---- traffic lights -----------------------------------------------------------------
  const tlPoleGeo = mergeColored([
    [new THREE.CylinderGeometry(0.07, 0.09, 3.3, 8), mat(0, 1.65, 0), 0x3a3d42],
    [new THREE.BoxGeometry(0.34, 1.0, 0.3), mat(0, 3.75, 0), 0x1f2226],
    [new THREE.BoxGeometry(0.42, 0.08, 0.36), mat(0, 4.3, 0.02), 0x1f2226],
  ]);
  const tlItems: { m: THREE.Matrix4; ns: boolean }[] = [];
  const o = HALF + 0.75;
  for (const n of L.nodes) {
    if (!n) continue;
    const X = n.x, Z = n.z;
    if (n.w) tlItems.push({ m: mat(X - o, CURB_H, Z + o, -Math.PI / 2), ns: false });
    if (n.e) tlItems.push({ m: mat(X + o, CURB_H, Z - o, Math.PI / 2), ns: false });
    if (n.n) tlItems.push({ m: mat(X - o, CURB_H, Z - o, Math.PI), ns: true });
    if (n.s) tlItems.push({ m: mat(X + o, CURB_H, Z + o, 0), ns: true });
  }
  const tlPoles = instanced(tlPoleGeo, poleMat, tlItems.map((t) => t.m), 'trafficLightPoles');
  meshes.push(tlPoles);
  ctx.raycast.push(tlPoles);
  const lampGeo = new THREE.IcosahedronGeometry(0.13, 0);
  const lampMat = new THREE.MeshBasicMaterial({ color: 0xffffff, toneMapped: false });
  const off = new THREE.Matrix4();
  const mkLights = (y: number) =>
    instanced(
      lampGeo,
      lampMat,
      tlItems.map((t) => t.m.clone().multiply(off.makeTranslation(0, y, 0.17))),
      'trafficLamps',
      false,
    );
  const red = mkLights(4.05), amber = mkLights(3.75), green = mkLights(3.45);
  meshes.push(red, amber, green);
  const DIM = new THREE.Color(0.06, 0.06, 0.06);
  const RED = new THREE.Color(3.2, 0.12, 0.08), AMB = new THREE.Color(3.0, 1.6, 0.15), GRN = new THREE.Color(0.15, 3.0, 0.5);
  let phase = -1;
  let tlTime = 0;
  const applyPhase = (p: number) => {
    // 0: NS green, 1: NS amber, 2: all red, 3: EW green, 4: EW amber, 5: all red
    tlItems.forEach((t, i) => {
      const grp = t.ns ? 0 : 3;
      const g = (p === grp) as boolean, a = p === grp + 1, r = !g && !a;
      red.setColorAt(i, r ? RED : DIM);
      amber.setColorAt(i, a ? AMB : DIM);
      green.setColorAt(i, g ? GRN : DIM);
    });
    red.instanceColor!.needsUpdate = true;
    amber.instanceColor!.needsUpdate = true;
    green.instanceColor!.needsUpdate = true;
  };
  ctx.updaters.push((dt) => {
    tlTime = (tlTime + dt) % 19;
    const p = tlTime < 7 ? 0 : tlTime < 8.5 ? 1 : tlTime < 9.5 ? 2 : tlTime < 16.5 ? 3 : tlTime < 18 ? 4 : 5;
    if (p !== phase) {
      phase = p;
      applyPhase(p);
    }
  });
  applyPhase(0);
  phase = 0;

  // ---- parking spots --------------------------------------------------------------------
  for (const s of L.segments) {
    if (s.bridge) continue;
    const len = s.dir === 'h' ? s.bx - s.ax : s.bz - s.az;
    for (const side of [-1, 1]) {
      if (rng() > 0.55) continue;
      for (let d = HALF + 3; d < len - HALF - 3; d += 6) {
        if (rng() < 0.15) continue;
        if (s.dir === 'h') {
          const x = s.ax + d, z = s.az + side * (HALF - 1.3);
          // right-hand: cars on the +z side travel +x, on the -z side travel -x
          ctx.parking.push({ pos: new THREE.Vector3(x, 0, z), heading: side > 0 ? Math.PI / 2 : -Math.PI / 2 });
        } else {
          const z = s.az + d, x = s.ax + side * (HALF - 1.3);
          // -x side travels +z (heading 0), +x side travels -z (heading PI)
          ctx.parking.push({ pos: new THREE.Vector3(x, 0, z), heading: side < 0 ? 0 : Math.PI });
        }
      }
    }
  }

  // ---- manholes ------------------------------------------------------------------------------
  const mhGeo = new THREE.CircleGeometry(0.5, 14);
  mhGeo.rotateX(-Math.PI / 2);
  const mhItems: THREE.Matrix4[] = [];
  for (const s of L.segments) {
    if (s.bridge || rng() < 0.4) continue;
    const len = s.dir === 'h' ? s.bx - s.ax : s.bz - s.az;
    const d = HALF + 4 + rng() * (len - HALF * 2 - 8);
    const lat = (rng() < 0.5 ? -1 : 1) * (rng() < 0.5 ? LANE : 0.3);
    if (s.dir === 'h') mhItems.push(mat(s.ax + d, 0.015, s.az + lat));
    else mhItems.push(mat(s.ax + lat, 0.015, s.az + d));
  }
  const mh = instanced(mhGeo, new THREE.MeshStandardMaterial({ map: T.manhole, transparent: true, roughness: 0.7, metalness: 0.3, polygonOffset: true, polygonOffsetFactor: -1, polygonOffsetUnits: -1 }), mhItems, 'manholes', false);
  mh.renderOrder = 1;
  meshes.push(mh);

  // ---- billboards ------------------------------------------------------------------------------
  const bbSoup = new QuadSoup('billboards');
  const B = L.bounds;
  // roadside billboards on the outer strip, facing the ring road
  const roadside: { x: number; y: number; z: number; ry: number; w: number }[] = [];
  for (let i = 0; i < 4; i++) {
    roadside.push({ x: L.roadX[0] - HALF - 6, y: 6.5, z: L.roadZ[1 + i * 3] + 30, ry: Math.PI / 2, w: 10 });
    roadside.push({ x: L.roadX[L.cols] + HALF + 6, y: 6.5, z: L.roadZ[2 + i * 2] + 30, ry: -Math.PI / 2, w: 10 });
    roadside.push({ x: L.roadX[1 + i * 3] + 30, y: 6.5, z: L.roadZ[0] - HALF - 6, ry: 0, w: 10 });
    roadside.push({ x: L.roadX[2 + i * 3] + 30, y: 6.5, z: L.roadZ[L.rows] + HALF + 6, ry: Math.PI, w: 10 });
  }
  void B;
  const placeBillboard = (r: { x: number; y: number; z: number; ry: number; w: number; idx?: number }, posts: boolean) => {
    const e = T.billboards.entries[(r.idx ?? Math.floor(rng() * T.billboards.entries.length)) % T.billboards.entries.length];
    const w = r.w, h = w / e.aspect;
    const nx = Math.sin(r.ry), nz = Math.cos(r.ry);
    const rx = Math.cos(r.ry), rz = -Math.sin(r.ry); // right vector (viewer looking at the face)
    const x0 = r.x - (rx * w) / 2, z0 = r.z - (rz * w) / 2, x1 = r.x + (rx * w) / 2, z1 = r.z + (rz * w) / 2;
    const y0 = r.y, y1 = r.y + h;
    bbSoup.quad(x0, y0, z0, x1, y0, z1, x1, y1, z1, x0, y1, z0, e.u0, e.v0, e.u1, e.v0, e.u1, e.v1, e.u0, e.v1);
    // backing board + frame
    ctx.metal.add(new THREE.BoxGeometry(w + 0.3, h + 0.3, 0.16), mat(r.x - nx * 0.1, y0 + h / 2, r.z - nz * 0.1, r.ry), 0x2b2e33);
    if (posts) {
      for (const sgn of [-1, 1]) ctx.metal.cyl(r.x + rx * sgn * w * 0.3, 0, r.z + rz * sgn * w * 0.3, 0.12, 0.16, y0, 0x555a60, 8);
      ctx.colliders.push(aabb(Math.min(x0, x1) - 0.3, Math.min(z0, z1) - 0.3, Math.max(x0, x1) + 0.3, Math.max(z0, z1) + 0.3, y1));
    } else {
      for (const sgn of [-1, 1]) ctx.metal.box(r.x + rx * sgn * w * 0.35 - nx * 0.5, y0 - 3.5, r.z + rz * sgn * w * 0.35 - nz * 0.5, 0.15, 3.6, 0.15, 0x555a60);
    }
  };
  for (const r of roadside) placeBillboard(r, true);
  for (const r of ctx.billboardRequests) placeBillboard(r, false);
  const bbMat = new THREE.MeshStandardMaterial({ map: T.billboards.map, emissiveMap: T.billboards.map, emissive: 0xffffff, emissiveIntensity: 0, roughness: 0.6, metalness: 0, vertexColors: true });
  const bbGeo = bbSoup.build();
  if (bbGeo) {
    const bb = new THREE.Mesh(bbGeo, bbMat);
    bb.name = 'billboards';
    meshes.push(bb);
    ctx.raycast.push(bb);
  }
  ctx.nightItems.push((f) => (bbMat.emissiveIntensity = f * 1.1));

  // ---- neon signs (two flicker groups) --------------------------------------------------------
  const neonSoups = [new QuadSoup('neonA'), new QuadSoup('neonB')];
  const placeSign = (soup: QuadSoup, atlas: TextAtlas, r: { x: number; y: number; z: number; ry: number; w: number; idx: number }, board: boolean) => {
    const e = atlas.entries[r.idx % atlas.entries.length];
    const w = r.w, h = w / e.aspect;
    const nx = Math.sin(r.ry), nz = Math.cos(r.ry);
    const rx = Math.cos(r.ry), rz = -Math.sin(r.ry);
    const x0 = r.x - (rx * w) / 2, z0 = r.z - (rz * w) / 2, x1 = r.x + (rx * w) / 2, z1 = r.z + (rz * w) / 2;
    const y0 = r.y - h / 2, y1 = r.y + h / 2;
    soup.quad(x0, y0, z0, x1, y0, z1, x1, y1, z1, x0, y1, z0, e.u0, e.v0, e.u1, e.v0, e.u1, e.v1, e.u0, e.v1);
    if (board) ctx.solid.add(new THREE.BoxGeometry(w + 0.2, h + 0.2, 0.12), mat(r.x - nx * 0.09, r.y, r.z - nz * 0.09, r.ry), 0x141416);
  };
  ctx.neonRequests.forEach((r, i) => placeSign(neonSoups[i % 2], T.neon, r, true));
  const neonMats = [0, 1].map(() => new THREE.MeshBasicMaterial({ map: T.neon.map, transparent: true, alphaTest: 0.04, depthWrite: false, side: THREE.DoubleSide, fog: false, toneMapped: false }));
  neonSoups.forEach((s, i) => {
    const g = s.build();
    if (!g) return;
    const m = new THREE.Mesh(g, neonMats[i]);
    m.name = 'neon' + i;
    m.renderOrder = 3;
    meshes.push(m);
  });
  let neonF = 0;
  ctx.nightItems.push((f) => (neonF = f));
  ctx.updaters.push((_dt, t) => {
    for (let i = 0; i < 2; i++) {
      const ph = i * 1.7;
      const flick = 0.9 + 0.1 * Math.sin(t * 9 + ph) * Math.sin(t * 2.3 + ph);
      const drop = Math.sin(t * 0.7 + ph * 3) > 0.985 ? 0.25 : 1;
      const k = (0.25 + 2.3 * neonF) * flick * drop;
      neonMats[i].color.setScalar(k);
    }
  });

  // ---- landmark signs ------------------------------------------------------------------------------
  const signSoup = new QuadSoup('signs');
  for (const r of ctx.signRequests) placeSign(signSoup, T.signs, r, false);
  const signMat = new THREE.MeshStandardMaterial({ map: T.signs.map, emissiveMap: T.signs.map, emissive: 0xffffff, emissiveIntensity: 0, roughness: 0.5, metalness: 0, vertexColors: true, side: THREE.DoubleSide });
  const signGeo = signSoup.build();
  if (signGeo) {
    const m = new THREE.Mesh(signGeo, signMat);
    m.name = 'signs';
    meshes.push(m);
  }
  ctx.nightItems.push((f) => (signMat.emissiveIntensity = 0.15 + f * 1.4));

  // ---- string lights ---------------------------------------------------------------------------------
  if (ctx.bulbRequests.length) {
    const bGeo = new THREE.SphereGeometry(0.09, 6, 5);
    const bMat = new THREE.MeshBasicMaterial({ color: 0xffd39a, toneMapped: false, fog: false });
    const bm = instanced(bGeo, bMat, ctx.bulbRequests.map((b) => mat(b.x, b.y, b.z)), 'stringBulbs', false);
    meshes.push(bm);
    const wGeo = new THREE.BufferGeometry();
    wGeo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(ctx.wires), 3));
    const wires = new THREE.LineSegments(wGeo, new THREE.LineBasicMaterial({ color: 0x1a1a1c }));
    wires.name = 'stringWires';
    meshes.push(wires);
    ctx.nightItems.push((f) => bMat.color.setScalar(0.5 + f * 2.6));
  }

  // ---- containers -----------------------------------------------------------------------------------
  if (ctx.containerRequests.length) {
    const cGeo = new THREE.BoxGeometry(6.06, 2.6, 2.44);
    const cMat = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.7, metalness: 0.25 });
    const cm = instanced(cGeo, cMat, ctx.containerRequests.map((c) => mat(c.x, c.y + 1.3, c.z, c.ry)), 'containers');
    ctx.containerRequests.forEach((c, i) => cm.setColorAt(i, new THREE.Color(c.color)));
    if (cm.instanceColor) cm.instanceColor.needsUpdate = true;
    meshes.push(cm);
    ctx.raycast.push(cm);
  }

  return { meshes };
}

export type { Layout };
export { SIDEWALK };
